import React, { useState, useEffect, useRef, useCallback } from 'react';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import chatService from '../../services/chatService';
import pusherService from '../../services/pusherService';
import { useAuth } from '../../contexts/AuthContext';
import './ChatWindow.css';

const ChatWindow = ({ room, onNewMessage }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);

  // Format last seen time
  const formatLastSeen = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString();
  };
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const roomChannelRef = useRef(null);
  const roomChannelHandlersRef = useRef(null); // Store event handlers for proper cleanup
  const pendingMessagesRef = useRef(new Map()); // Track pending optimistic messages
  const prevRoomIdRef = useRef(null); // Track previous room ID to prevent unnecessary reloads
  const markAsReadTimeoutRef = useRef(null); // Debounce mark as read calls
  const observedMessagesRef = useRef(new Set()); // Track which messages we've observed
  const currentRoomIdRef = useRef(null); // Track current room ID for event handlers

  const markMessagesAsRead = useCallback(async () => {
    if (!room) return;
    
    try {
      await chatService.markMessagesAsRead(room.id);
      // Update message read status locally for messages from others
      setMessages(prev => prev.map(msg => ({
        ...msg,
        is_read: msg.user_id === user?.id ? msg.is_read : true
      })));
      // Don't call onRoomUpdate - let Pusher events handle the update
    } catch (err) {
      console.error('Error marking messages as read:', err);
    }
  }, [room?.id, user?.id]);

  // Mark messages as read when they become visible (scroll-based)
  useEffect(() => {
    if (!room?.id || !messagesContainerRef.current) {
      return;
    }

    const container = messagesContainerRef.current;
    const messageElements = container.querySelectorAll('[data-message-id]');
    
    if (messageElements.length === 0) {
      return;
    }

    // Create Intersection Observer to detect when messages are visible
    const observer = new IntersectionObserver(
      (entries) => {
        let hasUnreadVisible = false;
        
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const messageElement = entry.target;
            const messageId = messageElement.getAttribute('data-message-id');
            
            if (messageId && !observedMessagesRef.current.has(messageId)) {
              // Find the message in our state
              const message = messages.find(msg => String(msg.id) === messageId);
              
              // Check if this is an unread message from others
              if (message && message.user_id !== user?.id && !message.is_read) {
                hasUnreadVisible = true;
                observedMessagesRef.current.add(messageId);
              }
            }
          }
        });

        // Debounce mark as read call
        if (hasUnreadVisible) {
          if (markAsReadTimeoutRef.current) {
            clearTimeout(markAsReadTimeoutRef.current);
          }
          
          markAsReadTimeoutRef.current = setTimeout(() => {
            // Mark all unread messages as read
            markMessagesAsRead();
          }, 500); // 500ms debounce
        }
      },
      {
        root: container,
        rootMargin: '0px',
        threshold: 0.1 // Trigger when 10% of message is visible
      }
    );

    // Observe all message elements
    messageElements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      observer.disconnect();
      if (markAsReadTimeoutRef.current) {
        clearTimeout(markAsReadTimeoutRef.current);
      }
    };
  }, [messages, room?.id, user?.id, markMessagesAsRead]);

  useEffect(() => {
    // Update current room ID ref
    currentRoomIdRef.current = room?.id || null;
    
    // Only reload messages if the room ID actually changed (not just room object reference)
    if (room && room.id !== prevRoomIdRef.current) {
      prevRoomIdRef.current = room.id;
      // Clear previous messages and show loading immediately for better UX
      setMessages([]);
      setLoading(true);
      setHasMore(true);
      // Reset observed messages when room changes
      observedMessagesRef.current.clear();
      // Load messages for the new room (latest 50)
      loadMessages();
      subscribeToRoomChannel();
    } else if (!room) {
      prevRoomIdRef.current = null;
      setMessages([]);
      setLoading(false);
      setHasMore(false);
      unsubscribeFromRoomChannel();
      observedMessagesRef.current.clear();
    }
    // If room.id hasn't changed, don't reload - just keep existing messages

    return () => {
      if (!room || room.id !== prevRoomIdRef.current) {
        unsubscribeFromRoomChannel();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room?.id]); // Only depend on room.id to prevent reloads when room object reference changes

  useEffect(() => {
    // Listen for new messages via Pusher
    const handleNewMessage = (event) => {
      const data = event.detail;
      
      // Use ref to get current room ID (avoids stale closure)
      const currentRoomId = currentRoomIdRef.current;
      if (!currentRoomId) return; // No room selected, ignore
      
      // Get room ID from data (check both possible locations)
      const messageRoomId = data.room_id || data.message?.chat_room_id;
      
      // If no room ID found in message, ignore it
      if (!messageRoomId) {
        return;
      }
      
      // Strict comparison - ensure message is for current room
      const currentRoomIdStr = String(currentRoomId);
      const msgRoomIdStr = String(messageRoomId);
      
      if (msgRoomIdStr !== currentRoomIdStr) {
        // Message is for a different room, ignore it
        console.log('[ChatWindow] Ignoring message - room mismatch:', {
          messageRoomId: msgRoomIdStr,
          currentRoomId: currentRoomIdStr,
          data: data
        });
        return;
      }
      
      // Also check the message object itself if it exists
      const newMessage = data.message || data;
      if (newMessage) {
        const messageChatRoomId = newMessage.chat_room_id || newMessage.room_id;
        if (messageChatRoomId && String(messageChatRoomId) !== currentRoomIdStr) {
          // Message object has different room ID, ignore it
          console.log('[ChatWindow] Ignoring message - message object room mismatch:', {
            messageChatRoomId: String(messageChatRoomId),
            currentRoomId: currentRoomIdStr
          });
          return;
        }
      }
      
      if (newMessage && newMessage.id) {
          // Skip if this is our own message - we already have it optimistically
          if (newMessage.user_id === user?.id) {
            // Update the optimistic message in place instead of replacing it
            const pendingKey = newMessage.message?.substring(0, 50) + newMessage.user_id;
            if (pendingMessagesRef.current.has(pendingKey)) {
              setMessages(prev => {
                // Find and update the optimistic message in place
                return prev.map(msg => {
                  if (msg.temp_id === pendingKey) {
                    // Update the optimistic message with real message data
                    return {
                      ...newMessage,
                      // Keep the same position, just update the data
                    };
                  }
                  return msg;
                });
              });
              pendingMessagesRef.current.delete(pendingKey);
            }
            return; // Don't add own message again
          }
          
          setMessages(prev => {
            // Check if message already exists
            if (prev.some(msg => msg.id === newMessage.id)) {
              return prev;
            }
            
            // If this is a pending optimistic message, replace it with the real one
            const pendingKey = newMessage.message?.substring(0, 50) + newMessage.user_id;
            if (pendingMessagesRef.current.has(pendingKey)) {
              pendingMessagesRef.current.delete(pendingKey);
              // Remove the pending message and add the real one - NO RELOAD
              return prev
                .filter(msg => msg.temp_id !== pendingKey)
                .concat(newMessage);
            }
            
            // Add new message without reloading - just update state
            return [...prev, newMessage];
          });
          scrollToBottom();
          // Don't call onNewMessage - let Pusher events in ChatLayout handle updates
        }
    };

    // Listen for messages read event (when peer reads messages we sent)
    const handleMessagesRead = (event) => {
      const data = event.detail;
      const currentRoomId = currentRoomIdRef.current;
      if (data && data.room_id && String(data.room_id) === String(currentRoomId) && data.user_id !== user?.id) {
        // Update read status only for messages that are currently unread
        // This is for read receipts - showing that the peer has read our messages
        setMessages(prev => prev.map(msg => {
          // Only update our own messages that are not yet marked as read
          if (msg.user_id === user?.id && !msg.is_read) {
            return { ...msg, is_read: true };
          }
          return msg;
        }));
      }
    };

    window.addEventListener('pusher:new-message', handleNewMessage);
    window.addEventListener('pusher:messages-read', handleMessagesRead);

    return () => {
      window.removeEventListener('pusher:new-message', handleNewMessage);
      window.removeEventListener('pusher:messages-read', handleMessagesRead);
    };
  }, [room?.id, user?.id]); // Added user?.id to check for own messages

  const loadMessages = async (beforeId = null) => {
    if (!room || loading) return;

    try {
      setLoading(true);
      // Always fetch latest 50 messages on initial load (when beforeId is null)
      const response = await chatService.getMessages(room.id, {
        limit: 50,
        before_id: beforeId, // null for initial load (latest 50), message ID for loading older messages
      });

      if (response.success) {
        if (beforeId) {
          // Loading older messages - prepend to existing messages
          setMessages(prev => {
            const newMessages = (response.data || []).map(msg => ({
              ...msg,
              is_read: msg.user_id === user?.id 
                ? (msg.is_read_receipt === true) 
                : msg.is_read
            }));
            return [...newMessages, ...prev];
          });
        } else {
          // Initial load - replace all messages with latest 50
          // Use is_read_receipt from backend for our own messages
          const loadedMessages = (response.data || []).map(msg => ({
            ...msg,
            // For our own messages, use is_read_receipt from backend; for others, use is_read
            is_read: msg.user_id === user?.id 
              ? (msg.is_read_receipt === true) 
              : msg.is_read
          }));
          setMessages(loadedMessages);
          scrollToBottom();
        }
        // Set hasMore based on whether we got a full page of results
        setHasMore(response.data && response.data.length === 50);
      }
    } catch (err) {
      console.error('Error loading messages:', err);
      setMessages([]); // Clear messages on error
    } finally {
      setLoading(false);
    }
  };

  const subscribeToRoomChannel = () => {
    if (!room?.id || !pusherService.getPusher()) return;

    const channelName = `chat-room-${room.id}`;
    const pusher = pusherService.getPusher();
    
    // Check if already subscribed to this channel
    if (roomChannelRef.current && roomChannelRef.current.name === channelName) {
      return; // Already subscribed, skip
    }
    
    // Unsubscribe from previous channel if exists
    unsubscribeFromRoomChannel();
    
    try {
      const channel = pusher.subscribe(channelName);
      roomChannelRef.current = channel;

      // Store handler reference for proper cleanup
      const newMessageHandler = (data) => {
        if (data.message) {
          // Double-check that message belongs to current room (safety check)
          const messageRoomId = data.message.chat_room_id || data.room_id;
          const currentRoomId = String(room.id);
          
          if (!messageRoomId) {
            console.warn('[ChatWindow] Message has no room ID in room channel:', data);
            return;
          }
          
          if (String(messageRoomId) !== currentRoomId) {
            console.log('[ChatWindow] Ignoring message from room channel - room mismatch:', {
              messageRoomId: String(messageRoomId),
              currentRoomId: currentRoomId,
              channel: channelName
            });
            return; // Message is for a different room, ignore it
          }
          
          // Skip if this is our own message - we already have it optimistically
          if (data.message.user_id === user?.id) {
            // Update the optimistic message in place instead of replacing it
            const pendingKey = data.message.message?.substring(0, 50) + data.message.user_id;
            if (pendingMessagesRef.current.has(pendingKey)) {
              setMessages(prev => {
                // Find and update the optimistic message in place
                return prev.map(msg => {
                  if (msg.temp_id === pendingKey) {
                    // Update the optimistic message with real message data
                    // Use is_read_receipt from backend for our own messages
                    return {
                      ...data.message,
                      is_read: data.message.user_id === user?.id 
                        ? (data.message.is_read_receipt === true)
                        : data.message.is_read,
                      // Keep the same position, just update the data
                    };
                  }
                  return msg;
                });
              });
              pendingMessagesRef.current.delete(pendingKey);
            }
            return; // Don't add own message again
          }
          
          // Final safety check: ensure message belongs to current room
          const finalMessageRoomId = data.message.chat_room_id || data.room_id;
          if (finalMessageRoomId && String(finalMessageRoomId) !== String(room.id)) {
            console.warn('Message room ID mismatch in room channel:', {
              messageRoomId: finalMessageRoomId,
              currentRoomId: room.id,
              channel: channelName
            });
            return; // Message doesn't belong to this room
          }
          
          setMessages(prev => {
            // Check if message already exists
            if (prev.some(msg => msg.id === data.message.id)) {
              // Update existing message with latest read receipt status
              return prev.map(msg => {
                if (msg.id === data.message.id) {
                  return {
                    ...data.message,
                    is_read: data.message.user_id === user?.id 
                      ? (data.message.is_read_receipt === true)
                      : data.message.is_read
                  };
                }
                return msg;
              });
            }
            
            // If this is a pending optimistic message, replace it with the real one
            const pendingKey = data.message.message?.substring(0, 50) + data.message.user_id;
            if (pendingMessagesRef.current.has(pendingKey)) {
              pendingMessagesRef.current.delete(pendingKey);
              // Remove the pending message and add the real one - NO RELOAD
              return prev
                .filter(msg => msg.temp_id !== pendingKey)
                .concat({
                  ...data.message,
                  is_read: data.message.user_id === user?.id 
                    ? (data.message.is_read_receipt === true)
                    : data.message.is_read
                });
            }
            
            // Add new message without reloading - just update state
            return [...prev, {
              ...data.message,
              is_read: data.message.user_id === user?.id 
                ? (data.message.is_read_receipt === true)
                : data.message.is_read
            }];
          });
          scrollToBottom();
        }
      };
      
      // Bind event and store handler reference
      channel.bind('new-message', newMessageHandler);
      roomChannelHandlersRef.current = {
        'new-message': newMessageHandler
      };
    } catch (err) {
      console.error('Error subscribing to room channel:', err);
    }
  };

  const unsubscribeFromRoomChannel = () => {
    if (roomChannelRef.current) {
      const pusher = pusherService.getPusher();
      if (pusher && roomChannelHandlersRef.current) {
        // Unbind all event handlers before unsubscribing
        Object.entries(roomChannelHandlersRef.current).forEach(([event, handler]) => {
          try {
            roomChannelRef.current.unbind(event, handler);
          } catch (err) {
            console.error(`[ChatWindow] Error unbinding ${event} handler:`, err);
          }
        });
        roomChannelHandlersRef.current = null;
      }
      
      if (pusher) {
        try {
          const channelName = roomChannelRef.current.name;
          pusher.unsubscribe(channelName);
          console.log('[ChatWindow] Unsubscribed from room channel:', channelName);
        } catch (err) {
          console.error('[ChatWindow] Error unsubscribing from room channel:', err);
        }
      }
      roomChannelRef.current = null;
    }
  };

  const handleSendMessage = async (message, type = 'text') => {
    if (!room || !message.trim()) return;

    // Create optimistic message (temporary message shown immediately)
    const tempId = `temp-${Date.now()}-${Math.random()}`;
    const pendingKey = message.substring(0, 50) + user?.id;
    const optimisticMessage = {
      id: tempId,
      temp_id: pendingKey,
      chat_room_id: room.id,
      user_id: user?.id,
      user_name: user?.name,
      user_profile_photo: user?.profile_photo,
      message: message,
      type: type,
      created_at: new Date().toISOString(),
      is_read: false,
      is_pending: true, // Flag to identify pending messages
    };

    // Add optimistic message immediately - NO RELOAD, just update state
    setMessages(prev => [...prev, optimisticMessage]);
    pendingMessagesRef.current.set(pendingKey, optimisticMessage);
    scrollToBottom();

    // Send message in background - don't block UI
    chatService.sendMessage(room.id, message, type)
      .then(response => {
        if (response.success) {
          // Message will be updated via Pusher event, which will update the optimistic one in place
          // If Pusher doesn't fire within 2 seconds, update manually
          setTimeout(() => {
            if (pendingMessagesRef.current.has(pendingKey) && response.data && response.data.id) {
              setMessages(prev => {
                // Update the optimistic message in place instead of replacing it
                return prev.map(msg => {
                  if (msg.temp_id === pendingKey) {
                    // Update the optimistic message with real message data
                    return {
                      ...response.data,
                      is_read: response.data.user_id === user?.id 
                        ? (response.data.is_read_receipt === true)
                        : response.data.is_read,
                      // Keep the same position, just update the data and remove pending flag
                    };
                  }
                  return msg;
                });
              });
              pendingMessagesRef.current.delete(pendingKey);
            }
          }, 2000);
        } else {
          // If send failed, remove optimistic message
          setMessages(prev => prev.filter(msg => msg.temp_id !== pendingKey));
          pendingMessagesRef.current.delete(pendingKey);
          console.error('Failed to send message');
        }
      })
      .catch(err => {
        console.error('Error sending message:', err);
        // Remove optimistic message on error
        setMessages(prev => prev.filter(msg => msg.temp_id !== pendingKey));
        pendingMessagesRef.current.delete(pendingKey);
      });
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      // Check if we're at bottom and mark messages as read
      checkAndMarkAsRead();
    }, 100);
  };

  // Check if user is at bottom and mark all messages as read
  const checkAndMarkAsRead = useCallback(() => {
    if (!room?.id || !messagesContainerRef.current) {
      return;
    }

    const container = messagesContainerRef.current;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    
    // Check if user is near bottom (within 100px)
    const isNearBottom = (scrollHeight - scrollTop - clientHeight) < 100;
    
    if (isNearBottom) {
      // Check if there are unread messages
      const hasUnread = messages.some(msg => 
        msg.user_id !== user?.id && !msg.is_read
      );
      
      if (hasUnread) {
        // Mark all messages as read when at bottom
        markMessagesAsRead();
      }
    }
  }, [room?.id, messages, user?.id, markMessagesAsRead]);

  const getRoomDisplayName = () => {
    if (!room) return '';
    if (room.name && !room.name.includes('&')) {
      return room.name;
    }
    if (room.peers && room.peers.length > 0) {
      return room.peers[0].name;
    }
    return room.name || 'Chat';
  };

  const getRoomAvatar = () => {
    if (!room) return null;
    if (room.peers && room.peers.length > 0) {
      return room.peers[0].profile_photo;
    }
    return null;
  };

  if (!room) {
    return (
      <div className="chat-window empty">
        <div className="chat-window-empty">
          <svg width="120" height="120" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          <h3>Select a chat to start messaging</h3>
          <p>Choose a conversation from the list to view messages</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-window">
      <div className="chat-window-header">
        <div className="chat-window-header-info">
          {getRoomAvatar() ? (
            <img src={getRoomAvatar()} alt={getRoomDisplayName()} />
          ) : (
            <div className="chat-window-avatar-placeholder">
              {getRoomDisplayName().charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <h3>{getRoomDisplayName()}</h3>
            {room.peers && room.peers.length > 0 && (
              <p className="chat-window-status">
                {room.peers[0].is_online ? (
                  <span className="online-status">
                    <span className="online-indicator"></span>
                    Online
                  </span>
                ) : room.peers[0].last_seen_at ? (
                  `Last seen ${formatLastSeen(room.peers[0].last_seen_at)}`
                ) : (
                  'Offline'
                )}
              </p>
            )}
          </div>
        </div>
      </div>

      <MessageList
        messages={messages}
        loading={loading}
        hasMore={hasMore}
        onLoadMore={() => {
          if (messages.length > 0) {
            loadMessages(messages[0].id);
          }
        }}
        messagesEndRef={messagesEndRef}
        messagesContainerRef={messagesContainerRef}
        onScroll={checkAndMarkAsRead}
      />

      <MessageInput
        onSendMessage={handleSendMessage}
      />
    </div>
  );
};

export default ChatWindow;

